import React, { useState, useEffect } from 'react'
import {
  usePeoAlignmentStats, useTracerStudyStats, useTracerTrends, useDepartmentComparison,
} from '../../hooks/useAnalytics'
import { supabaseClient } from '../../config/supabase'
import toast from 'react-hot-toast'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import {
  Sparkles, Loader2, AlertTriangle, RefreshCw, Download,
  TrendingUp, TrendingDown, Target, Briefcase, BarChart2, CheckCircle,
} from 'lucide-react'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000'

interface Props {
  department: string | null
  program: string | null
  batchYear: number | null
}

// ── Insight types (mirrors Python response schema) ─────────────────────────────
interface Strength  { title: string; detail: string; metric: string }
interface Weakness  { title: string; detail: string; metric: string }
interface PeoInsight { program_code: string; program_name?: string; weak_outcomes?: string; outcome_count?: number; status: 'needs_improvement' | 'critical'; insight: string; recommendation: string }
interface ActionItem { priority: 'high' | 'medium' | 'low'; action: string; rationale: string }

interface AiInsights {
  summary: string
  strengths: Strength[]
  weaknesses: Weakness[]
  peo_insights: PeoInsight[]
  employment_insights: { summary: string; key_findings: string[]; recommendations: string[] }
  comparative_insights: { vs_university: string; highlights: string[] }
  action_items: ActionItem[]
}

// ── Helpers ────────────────────────────────────────────────────────────────────
const PRIORITY_COLORS: Record<string, string> = {
  high:   'bg-red-100 text-red-700 border-red-200',
  medium: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  low:    'bg-blue-100 text-blue-700 border-blue-200',
}

const PEO_STATUS_COLORS: Record<string, string> = {
  strong:            'bg-green-100 text-green-700',
  needs_improvement: 'bg-yellow-100 text-yellow-700',
  critical:          'bg-red-100 text-red-700',
}

const PEO_STATUS_LABELS: Record<string, string> = {
  strong:            'Strong',
  needs_improvement: 'Needs Improvement',
  critical:          'Critical',
}

// ── Component ──────────────────────────────────────────────────────────────────
const InsightsTab: React.FC<Props> = ({ department, program, batchYear }) => {
  const { data: peoData,    loading: peoLoading    } = usePeoAlignmentStats(department, program, batchYear)
  const { data: tracerData, loading: tracerLoading } = useTracerStudyStats(department, program, batchYear)
  const { data: trendsData, loading: trendsLoading } = useTracerTrends(department)
  const { data: compData                           } = useDepartmentComparison()

  const dataLoading = peoLoading || tracerLoading || trendsLoading

  const [insights, setInsights]   = useState<AiInsights | null>(null)
  const [aiLoading, setAiLoading] = useState(false)
  const [error, setError]         = useState<string | null>(null)

  const cacheKey = `insights_${department ?? 'all'}_${program ?? 'all'}_${batchYear ?? 'all'}`

  // Restore cached insights when tab is re-entered or filters change
  useEffect(() => {
    try {
      const cached = sessionStorage.getItem(cacheKey)
      if (cached) setInsights(JSON.parse(cached))
      else setInsights(null)
    } catch { setInsights(null) }
  }, [cacheKey])

  const hasData = !!(peoData?.length || tracerData?.total_alumni)

  const handleGenerate = async () => {
    setAiLoading(true)
    setError(null)
    try {
      const { data: { session } } = await supabaseClient.auth.getSession()
      const token = session?.access_token
      if (!token) throw new Error('Not authenticated')

      const payload = {
        department,
        program,
        batch_year: batchYear,
        peo_data: peoData ?? [],
        tracer_stats: tracerData ?? null,
        trends: trendsData ?? [],
        department_comparison: compData ?? [],
      }

      const res = await fetch(`${API_URL}/api/analytics/insights`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      })

      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        throw new Error(body.error || `Request failed (${res.status})`)
      }

      const body = await res.json()
      setInsights(body.insights)
      try { sessionStorage.setItem(cacheKey, JSON.stringify(body.insights)) } catch { /* storage full */ }
    } catch (err: any) {
      setError(err.message || 'Failed to generate insights')
    } finally {
      setAiLoading(false)
    }
  }

  const sanitize = (s: string): string =>
    (s ?? '')
      .replace(/≥/g, '>=').replace(/≤/g, '<=').replace(/→/g, '->')
      .replace(/←/g, '<-').replace(/×/g, 'x').replace(/÷/g, '/')
      .replace(/–/g, '-').replace(/—/g, '--').replace(/"/g, '"')
      .replace(/"/g, '"').replace(/'/g, "'").replace(/'/g, "'")
      .replace(/•/g, '-').replace(/…/g, '...').replace(/°/g, ' degrees')
      .replace(/[^\x00-\x7F]/g, (c) => {
        // Replace any remaining non-ASCII with closest ASCII or remove
        const map: Record<string, string> = { 'é':'e','è':'e','ê':'e','ë':'e','à':'a','â':'a','ä':'a','ô':'o','ö':'o','ù':'u','û':'u','ü':'u','ç':'c','ñ':'n' }
        return map[c] ?? ''
      })

  const handleExport = async () => {
    if (!insights) { toast.error('Generate insights first'); return }

    const BLUE: [number, number, number]   = [30, 58, 95]
    const GREEN: [number, number, number]  = [21, 128, 61]
    const RED: [number, number, number]    = [185, 28, 28]
    const INDIGO: [number, number, number] = [67, 56, 202]
    const PURPLE: [number, number, number] = [109, 40, 217]
    const GRAY: [number, number, number]   = [75, 85, 99]
    const GRAY_L: [number, number, number] = [156, 163, 175]
    const W = 612, MARGIN = 72, CW = W - MARGIN * 2, H = 792

    const loadImg = async (url: string): Promise<string | undefined> => {
      try {
        const res = await fetch(url)
        const blob = await res.blob()
        return await new Promise<string>((resolve, reject) => {
          const reader = new FileReader()
          reader.onload = () => resolve(reader.result as string)
          reader.onerror = reject
          reader.readAsDataURL(blob)
        })
      } catch { return undefined }
    }

    const [ucLogo, tbLogo] = await Promise.all([
      loadImg('/uc-logo.png'),
      loadImg('/trailblaze2_logonobg.png'),
    ])

    const doc = new jsPDF({ unit: 'pt', format: 'letter' })
    const dateStr = new Date().toLocaleDateString('en-PH', { year: 'numeric', month: 'long', day: 'numeric' })
    const cx = W / 2

    const addHeaderFooter = (pageNum: number, title: string) => {
      doc.setFontSize(8); doc.setTextColor(...GRAY_L); doc.setFont('helvetica', 'normal')
      doc.text('UCLM', MARGIN, 40)
      doc.text(title, W - MARGIN, 40, { align: 'right' })
      doc.setDrawColor(...GRAY_L); doc.setLineWidth(0.5)
      doc.line(MARGIN, 48, W - MARGIN, 48)
      doc.setFontSize(7)
      doc.text('Confidential — For Internal Use Only', MARGIN, H - 30)
      doc.text(`Page ${pageNum}`, W - MARGIN, H - 30, { align: 'right' })
    }

    // ═══ Cover Page ═══════════════════════════════════════════
    if (ucLogo) { try { doc.addImage(ucLogo, 'PNG', MARGIN, 65, 72, 72) } catch (_) {} }
    if (tbLogo) {
      try {
        const tbW = Math.round(72 * (1334 / 967))
        doc.addImage(tbLogo, 'PNG', W - MARGIN - tbW, 65, tbW, 72)
      } catch (_) {}
    }
    doc.setDrawColor(...BLUE); doc.setLineWidth(1)
    doc.line(MARGIN, 155, W - MARGIN, 155)

    doc.setFontSize(22); doc.setTextColor(...BLUE); doc.setFont('times', 'bold')
    doc.text('University of Cebu Lapu-Lapu and Mandaue', cx, 228, { align: 'center' })
    doc.setLineWidth(2); doc.line(cx - 120, 244, cx + 120, 244)

    doc.setFontSize(18); doc.setTextColor(...GRAY); doc.setFont('times', 'bold')
    doc.text('Analytics Insights Report', cx, 282, { align: 'center' })

    if (department) {
      doc.setFontSize(13); doc.setTextColor(...GRAY_L); doc.setFont('times', 'normal')
      doc.text(department, cx, 310, { align: 'center' })
    }

    doc.setFontSize(10); doc.setTextColor(...GRAY); doc.setFont('times', 'normal')
    doc.text(`Date Generated: ${dateStr}`, cx, 380, { align: 'center' })

    // ═══ Content Pages ════════════════════════════════════════
    let pageNum = 1
    const nextPage = () => { doc.addPage(); pageNum++; addHeaderFooter(pageNum, 'Analytics Insights Report') }
    nextPage()

    const getY = () => (doc as any).lastAutoTable?.finalY ?? 72

    // helper: colored section label above each table
    const sectionLabel = (label: string, color: [number, number, number]) => {
      const y = getY() + 16
      doc.setFillColor(...color)
      doc.rect(MARGIN, y, CW, 20, 'F')
      doc.setTextColor(255, 255, 255); doc.setFontSize(10); doc.setFont('helvetica', 'bold')
      doc.text(label, MARGIN + 8, y + 14)
      return y + 20
    }

    // ── Executive Summary ─────────────────────────────────────
    if (insights.summary) {
      autoTable(doc, {
        startY: 72,
        margin: { left: MARGIN, right: MARGIN },
        head: [['Executive Summary']],
        body: [[sanitize(insights.summary)]],
        headStyles: { fillColor: INDIGO, fontSize: 10, fontStyle: 'bold' },
        bodyStyles: { fontSize: 9, textColor: [55, 48, 163], fillColor: [238, 242, 255] },
      })
    }

    // ── Strengths ─────────────────────────────────────────────
    if ((insights.strengths ?? []).length > 0) {
      const sy = sectionLabel('Strengths', GREEN)
      autoTable(doc, {
        startY: sy,
        margin: { left: MARGIN, right: MARGIN },
        head: [['Title', 'Metric', 'Detail']],
        body: (insights.strengths ?? []).map(s => [sanitize(s.title), sanitize(s.metric ?? ''), sanitize(s.detail)]),
        headStyles: { fillColor: GREEN, fontSize: 8, fontStyle: 'bold' },
        bodyStyles: { fontSize: 8 },
        columnStyles: { 0: { cellWidth: 120, fontStyle: 'bold', textColor: GREEN }, 1: { cellWidth: 130 }, 2: { cellWidth: CW - 250 } },
      })
    }

    // ── Weaknesses ────────────────────────────────────────────
    if ((insights.weaknesses ?? []).length > 0) {
      const sy = sectionLabel('Weaknesses', RED)
      autoTable(doc, {
        startY: sy,
        margin: { left: MARGIN, right: MARGIN },
        head: [['Title', 'Metric', 'Detail']],
        body: (insights.weaknesses ?? []).map(w => [sanitize(w.title), sanitize(w.metric ?? ''), sanitize(w.detail)]),
        headStyles: { fillColor: RED, fontSize: 8, fontStyle: 'bold' },
        bodyStyles: { fontSize: 8 },
        columnStyles: { 0: { cellWidth: 120, fontStyle: 'bold', textColor: RED }, 1: { cellWidth: 130 }, 2: { cellWidth: CW - 250 } },
      })
    }

    // ── PEO/PO Insights ───────────────────────────────────────
    if (actionablePeoInsights.length > 0) {
      const STATUS_COLORS: Record<string, [number, number, number]> = {
        strong: GREEN, needs_improvement: [161, 98, 7], critical: RED,
      }
      const sy = sectionLabel('PEO/PO Insights — Needs Attention', BLUE)
      autoTable(doc, {
        startY: sy,
        margin: { left: MARGIN, right: MARGIN },
        head: [['Program', 'Weak Outcomes', 'Status', 'Insight', 'Recommendation']],
        body: actionablePeoInsights.map(p => [
          `${p.program_code}${p.program_name ? `\n${p.program_name}` : ''}`,
          `${p.weak_outcomes ?? ''}${p.outcome_count ? ` (${p.outcome_count})` : ''}`,
          (p.status ?? '').replace(/_/g, ' '),
          sanitize(p.insight),
          sanitize(p.recommendation ?? ''),
        ]),
        headStyles: { fillColor: BLUE, fontSize: 8, fontStyle: 'bold' },
        bodyStyles: { fontSize: 7.5 },
        columnStyles: {
          0: { cellWidth: 75 },
          1: { cellWidth: 110 },
          2: { cellWidth: 55 },
          3: { cellWidth: 150 },
          4: { cellWidth: 78 },
        },
        didParseCell: (data) => {
          if (data.column.index === 2 && data.section === 'body') {
            const key = String(data.cell.raw ?? '').replace(/ /g, '_')
            data.cell.styles.textColor = STATUS_COLORS[key] ?? GRAY
            data.cell.styles.fontStyle = 'bold'
          }
        },
      })
    }

    // ── Employment Insights ───────────────────────────────────
    if (insights.employment_insights) {
      const empRows: [string, string][] = []
      if (insights.employment_insights.summary) empRows.push(['Overview', sanitize(insights.employment_insights.summary)])
      ;(insights.employment_insights.key_findings ?? []).forEach(f => empRows.push(['Finding', sanitize(f)]))
      ;(insights.employment_insights.recommendations ?? []).forEach(r => empRows.push(['Recommendation', r]))
      if (empRows.length > 0) {
        const sy = sectionLabel('Employment Insights', INDIGO)
        autoTable(doc, {
          startY: sy,
          margin: { left: MARGIN, right: MARGIN },
          head: [['Type', 'Detail']],
          body: empRows,
          headStyles: { fillColor: INDIGO, fontSize: 8, fontStyle: 'bold' },
          bodyStyles: { fontSize: 8 },
          columnStyles: { 0: { cellWidth: 90 } },
        })
      }
    }

    // ── Comparative Insights ──────────────────────────────────
    const compRows: [string, string][] = []
    if (insights.comparative_insights?.vs_university) compRows.push(['vs University', sanitize(insights.comparative_insights.vs_university)])
    ;(insights.comparative_insights?.highlights ?? []).forEach(h => compRows.push(['Highlight', sanitize(h)]))
    if (compRows.length > 0) {
      const sy = sectionLabel('Compared to Other Departments', [30, 64, 175])
      autoTable(doc, {
        startY: sy,
        margin: { left: MARGIN, right: MARGIN },
        head: [['Type', 'Detail']],
        body: compRows,
        headStyles: { fillColor: [30, 64, 175], fontSize: 8, fontStyle: 'bold' },
        bodyStyles: { fontSize: 8 },
        columnStyles: { 0: { cellWidth: 90 } },
      })
    }

    // ── Action Items ──────────────────────────────────────────
    if ((insights.action_items ?? []).length > 0) {
      const PRIORITY_COLORS: Record<string, [number, number, number]> = {
        high: RED, medium: [161, 98, 7], low: BLUE,
      }
      const sy = sectionLabel('Action Items', PURPLE)
      autoTable(doc, {
        startY: sy,
        margin: { left: MARGIN, right: MARGIN },
        head: [['Priority', 'Action', 'Rationale']],
        body: (insights.action_items ?? []).map(a => [
          (a.priority ?? '').toUpperCase(), sanitize(a.action), sanitize(a.rationale ?? ''),
        ]),
        headStyles: { fillColor: PURPLE, fontSize: 8, fontStyle: 'bold' },
        bodyStyles: { fontSize: 8 },
        columnStyles: { 0: { cellWidth: 55 }, 1: { cellWidth: 210 }, 2: { cellWidth: CW - 265 } },
        didParseCell: (data) => {
          if (data.column.index === 0 && data.section === 'body') {
            const p = String(data.cell.raw ?? '').toLowerCase()
            data.cell.styles.textColor = PRIORITY_COLORS[p] ?? GRAY
            data.cell.styles.fontStyle = 'bold'
          }
        },
      })
    }

    doc.save(`insights-${new Date().toISOString().slice(0, 10)}.pdf`)
    toast.success('Insights PDF exported')
  }

  // ── Header ────────────────────────────────────────────────────
  const header = (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 flex flex-col sm:flex-row sm:items-center gap-4">
      <div className="flex items-center gap-3 flex-1">
        <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center shrink-0">
          <Sparkles className="w-5 h-5 text-indigo-600" />
        </div>
        <div>
          <h2 className="text-base font-bold text-gray-900">Insights</h2>
          <p className="text-xs text-gray-400">Intelligent analysis · based on current filters</p>
        </div>
      </div>
      <button
        onClick={handleGenerate}
        disabled={aiLoading || dataLoading || !hasData}
        className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-300 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors shrink-0"
      >
        {aiLoading
          ? <><Loader2 className="w-4 h-4 animate-spin" /> Analyzing…</>
          : <><Sparkles className="w-4 h-4" /> Generate Insights</>
        }
      </button>
    </div>
  )

  // ── Loading / no data states ──────────────────────────────────
  if (dataLoading) {
    return (
      <div className="space-y-4">
        {header}
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-blue-900" />
          <span className="ml-2 text-sm text-gray-500">Loading analytics data…</span>
        </div>
      </div>
    )
  }

  if (!hasData) {
    return (
      <div className="space-y-4">
        {header}
        <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
          <Sparkles className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <h3 className="text-base font-bold text-gray-700 mb-1">No Data Available</h3>
          <p className="text-sm text-gray-500">There is no alumni or survey data to analyze for the current filters.</p>
        </div>
      </div>
    )
  }

  // ── Empty (not yet generated) ─────────────────────────────────
  if (!insights && !aiLoading) {
    return (
      <div className="space-y-4">
        {header}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5 shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-red-800">Failed to generate insights</p>
              <p className="text-xs text-red-600 mt-0.5">{error}</p>
            </div>
            <button onClick={handleGenerate} className="flex items-center gap-1 text-xs text-red-700 font-medium hover:underline">
              <RefreshCw className="w-3 h-3" /> Retry
            </button>
          </div>
        )}
        <div className="text-center py-16 bg-white rounded-xl border border-gray-200 border-dashed">
          <Sparkles className="w-12 h-12 text-indigo-200 mx-auto mb-3" />
          <h3 className="text-base font-bold text-gray-700 mb-1">Ready to Analyze</h3>
          <p className="text-sm text-gray-500 max-w-sm mx-auto">
            Click <span className="font-medium text-indigo-600">Generate Insights</span> to analyze your department's strengths, weaknesses, and PEO alignment.
          </p>
        </div>
      </div>
    )
  }

  // ── Insights display ──────────────────────────────────────────
  if (aiLoading) {
    return (
      <div className="space-y-4">
        {header}
        <div className="flex flex-col items-center justify-center py-20 gap-3">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
          <p className="text-sm font-medium text-gray-600">Analyzing your data…</p>
          <p className="text-xs text-gray-400">This takes about 20–30 seconds</p>
        </div>
      </div>
    )
  }

  if (!insights) return null

  const actionablePeoInsights = insights.peo_insights ?? []

  return (
    <div className="space-y-5">
      {/* Header with regenerate + export */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="flex items-center gap-3 flex-1">
          <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center shrink-0">
            <Sparkles className="w-5 h-5 text-indigo-600" />
          </div>
          <div>
            <h2 className="text-base font-bold text-gray-900">Insights</h2>
            <p className="text-xs text-gray-400">Intelligent analysis · based on current filters</p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={handleExport}
            className="flex items-center gap-1.5 text-xs font-medium text-gray-600 bg-gray-50 border border-gray-200 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <Download className="w-3.5 h-3.5" /> Export Insights
          </button>
          <button
            onClick={handleGenerate}
            disabled={aiLoading}
            className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-300 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
          >
            <RefreshCw className="w-4 h-4" /> Regenerate
          </button>
        </div>
      </div>

      {/* Summary */}
      <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-5">
        <p className="text-sm font-semibold text-indigo-900 mb-1">Executive Summary</p>
        <p className="text-sm text-indigo-800 leading-relaxed">{insights.summary}</p>
      </div>

      {/* Strengths & Weaknesses */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="flex items-center gap-2 px-5 py-3 bg-green-50 border-b border-green-100">
            <TrendingUp className="w-4 h-4 text-green-600" />
            <h3 className="text-sm font-bold text-green-800">Strengths</h3>
          </div>
          <div className="divide-y divide-gray-100">
            {(insights.strengths ?? []).map((s, i) => (
              <div key={i} className="px-5 py-4">
                <p className="text-sm font-semibold text-gray-800 mb-1">{s.title}</p>
                {s.metric && <span className="inline-block text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full mb-1.5 break-words max-w-full">{s.metric}</span>}
                <p className="text-xs text-gray-500 leading-relaxed">{s.detail}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="flex items-center gap-2 px-5 py-3 bg-red-50 border-b border-red-100">
            <TrendingDown className="w-4 h-4 text-red-600" />
            <h3 className="text-sm font-bold text-red-800">Weaknesses</h3>
          </div>
          <div className="divide-y divide-gray-100">
            {(insights.weaknesses ?? []).map((w, i) => (
              <div key={i} className="px-5 py-4">
                <p className="text-sm font-semibold text-gray-800 mb-1">{w.title}</p>
                {w.metric && <span className="inline-block text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full mb-1.5 break-words max-w-full">{w.metric}</span>}
                <p className="text-xs text-gray-500 leading-relaxed">{w.detail}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* PEO/PO Insights */}
      {actionablePeoInsights.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="flex items-center gap-2 px-5 py-3 bg-gray-50 border-b border-gray-200">
            <Target className="w-4 h-4 text-gray-600" />
            <h3 className="text-sm font-bold text-gray-700">PEO/PO Insights — Programs Needing Attention</h3>
            <span className="text-xs text-gray-400 ml-auto">{actionablePeoInsights.length} program{actionablePeoInsights.length !== 1 ? 's' : ''}</span>
          </div>
          <div className="divide-y divide-gray-100">
            {actionablePeoInsights.map((p, i) => (
              <div key={i} className="px-5 py-4">
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <p className="text-sm font-semibold text-gray-800">{p.program_code}</p>
                  {p.program_name && <span className="text-xs text-gray-500">{p.program_name}</span>}
                  {p.status && (
                    <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${PEO_STATUS_COLORS[p.status] ?? 'bg-gray-100 text-gray-700'}`}>
                      {PEO_STATUS_LABELS[p.status] ?? p.status}
                    </span>
                  )}
                </div>
                {p.weak_outcomes && (
                  <div className="flex items-center gap-1.5 mb-2 flex-wrap">
                    <span className="text-[10px] text-gray-400 font-medium">Weak outcomes:</span>
                    {p.weak_outcomes.split(',').map((o, j) => (
                      <span key={j} className="text-[10px] font-bold bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded">{o.trim()}</span>
                    ))}
                  </div>
                )}
                <p className="text-xs text-gray-600 mb-2 leading-relaxed">{p.insight}</p>
                {p.recommendation && (
                  <div className="bg-blue-50 rounded-lg px-3 py-2">
                    <p className="text-xs text-blue-800"><span className="font-semibold">Recommendation:</span> {p.recommendation}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Employment Insights */}
      {insights.employment_insights && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="flex items-center gap-2 px-5 py-3 bg-gray-50 border-b border-gray-200">
            <Briefcase className="w-4 h-4 text-gray-600" />
            <h3 className="text-sm font-bold text-gray-700">Employment Insights</h3>
          </div>
          <div className="px-5 py-4 space-y-4">
            {insights.employment_insights.summary && (
              <p className="text-sm text-gray-700 leading-relaxed">{insights.employment_insights.summary}</p>
            )}
            {(insights.employment_insights.key_findings ?? []).length > 0 && (
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Key Findings</p>
                <ul className="space-y-1">
                  {(insights.employment_insights.key_findings ?? []).map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-xs text-gray-600">
                      <span className="text-indigo-500 mt-0.5 shrink-0">•</span>{f}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {(insights.employment_insights.recommendations ?? []).length > 0 && (
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Recommendations</p>
                <ul className="space-y-1">
                  {(insights.employment_insights.recommendations ?? []).map((r, i) => (
                    <li key={i} className="flex items-start gap-2 text-xs text-gray-600">
                      <CheckCircle className="w-3.5 h-3.5 text-green-500 mt-0.5 shrink-0" />{r}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Comparative Insights */}
      {insights.comparative_insights && (insights.comparative_insights.vs_university || (insights.comparative_insights.highlights ?? []).length > 0) && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="flex items-center gap-2 px-5 py-3 bg-gray-50 border-b border-gray-200">
            <BarChart2 className="w-4 h-4 text-gray-600" />
            <h3 className="text-sm font-bold text-gray-700">Compared to Other Departments</h3>
          </div>
          <div className="px-5 py-4 space-y-3">
            {insights.comparative_insights.vs_university && (
              <p className="text-sm text-gray-700 leading-relaxed">{insights.comparative_insights.vs_university}</p>
            )}
            {(insights.comparative_insights.highlights ?? []).length > 0 && (
              <ul className="space-y-1">
                {(insights.comparative_insights.highlights ?? []).map((h, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-gray-600">
                    <span className="text-indigo-500 mt-0.5 shrink-0">•</span>{h}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {/* Action Items */}
      {(insights.action_items ?? []).length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="flex items-center gap-2 px-5 py-3 bg-gray-50 border-b border-gray-200">
            <CheckCircle className="w-4 h-4 text-gray-600" />
            <h3 className="text-sm font-bold text-gray-700">Action Items</h3>
          </div>
          <div className="divide-y divide-gray-100">
            {(insights.action_items ?? []).map((a, i) => (
              <div key={i} className="flex items-start gap-3 px-5 py-4">
                <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full border shrink-0 mt-0.5 ${PRIORITY_COLORS[a.priority]}`}>
                  {a.priority}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-800 mb-0.5">{a.action}</p>
                  <p className="text-xs text-gray-500 leading-relaxed">{a.rationale}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default InsightsTab
